import { FC, ReactNode, useEffect, useState } from 'react'

import { usePrevious } from '@sequencehq/utils'
import {
  IconSignal,
  EmptyState,
  GreyGrey30,
  GreyGrey70,
  Spinner,
  StandardLayout,
  Center,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'

import { InboxListSwitch } from './InboxListSwitch'

const InboxListContainer: FC<{
  headerActions?: ReactNode
  filterRow?: ReactNode
  children: ReactNode
  isPending: boolean
  'data-testid'?: string
}> = ({
  headerActions,
  filterRow,
  children,
  isPending,
  'data-testid': dataTestId
}) => {
  /**
   * To prevent too much UI churn, we only fade in the loading state after a small
   * delay.
   */
  const [showLoadingComponent, setShowLoadingComponent] = useState(false)
  const prevIsPending = usePrevious(isPending)

  useEffect(() => {
    if (isPending && !prevIsPending) {
      setTimeout(() => setShowLoadingComponent(true), 250)
    }
    if (!isPending) {
      setShowLoadingComponent(false)
    }
  }, [isPending, prevIsPending])

  return (
    <Wrapper
      headerActions={headerActions}
      filterRow={filterRow}
      data-testid={dataTestId}
    >
      <Grid
        gridTemplateRows="auto 1fr"
        overflow="hidden"
        width="100%"
        // Fill the space left by the header/filter row rather than the full
        // wrapper height (`minHeight: 0` lets the inner list actually shrink and
        // scroll). Otherwise the list overflows the panel and — on mobile, where
        // the surrounding content area is `overflow: auto` — the whole page
        // scrolls instead of the list.
        flex="1"
        minHeight="0"
      >
        <Box padding="8px">
          <InboxListSwitch />
        </Box>
        {isPending ? (
          <Center
            width="100%"
            opacity={showLoadingComponent ? 1 : 0}
            transition="opacity 250ms"
            height="100%"
          >
            <Spinner color={GreyGrey70} />
          </Center>
        ) : (
          <Box
            padding="0 8px 8px"
            overflowY="auto"
            overscrollBehavior="contain"
          >
            {children}
          </Box>
        )}
      </Grid>
    </Wrapper>
  )
}

const Wrapper: FC<{
  headerActions?: ReactNode
  filterRow?: ReactNode
  children: ReactNode
  'data-testid'?: string
}> = ({ headerActions, filterRow, children, 'data-testid': dataTestId }) => {
  return (
    <Flex
      flexDir="column"
      height="100%"
      borderRight={{ base: 'none', md: `1px solid ${GreyGrey30}` }}
      width={{ base: '100%', md: '400px' }}
      data-testid={dataTestId}
    >
      <StandardLayout.Header title="Watchtower" actions={headerActions} />
      {filterRow}
      {children}
    </Flex>
  )
}

const InboxEmptyState: FC = () => {
  return (
    <EmptyState
      icon={IconSignal}
      title="Inbox zero"
      body="You're all caught up. Any new tasks that need your input will appear here."
      data-testid="watchtower.inbox.emptyState"
    />
  )
}

export const InboxList = Object.assign(InboxListContainer, {
  EmptyState: InboxEmptyState
})
